<template>
  <div>
    <v-navigation-drawer
      v-model="open"
      :width="$isMobile ? '100%' : '50%'"
      right
      fixed
      :permanent="open"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex">
          <v-btn
            dark
            fab
            x-small
            outlined
            color="primary"
            class="mr-3"
            @click="closePanel"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="subtitle-1 primary--text my-auto">
            {{ $t('expenses.registerMovements.invoices.drawerRelate.title') }}
          </div>
        </div>
        <div>
          <div>
            <v-row>
              <v-col md="12">
                <v-data-table
                  v-model="table.selected"
                  :headers="table.headers"
                  :items="table.data"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 30, 40, 50]
                  }"
                  :items-per-page="10"
                  show-select
                  single-select
                  class="elevation-1 fixed-header">
                  <template v-slot:[`item._id`]="{ item }">
                    <div>
                      {{ item._id }}
                    </div>
                  </template>
                  <template v-slot:[`item.total`]="{ item }">
                    <div>
                      ${{ item.total | toFix2 | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:[`item.paymentMethod`]="{ item }">
                    <div>
                      {{ PAYORDER_PAYMENT_TYPES[item.paymentMethod] }}
                    </div>
                  </template>
                  <template v-slot:[`item.createdAt`]="{ item }">
                    <div>
                      {{ $moment(item.createdAt).format('DD/MM/YYYY') }}
                    </div>
                  </template>
                  <template v-slot:[`item.status`]="{ item }">
                    <div>
                      {{ PAYORDER_STATUS_TYPES[item.status] }}
                    </div>
                  </template>
                </v-data-table>
              </v-col>
              <v-col md="12" class="d-flex justify-end">
                <v-btn
                  v-if="invoiceBoxPermissions['relate-pay-order']"
                  color="primary"
                  depressed
                  :disabled="!table.selected.length"
                  @click="relatePayOrder()">
                  {{ $t('expenses.registerMovements.invoices.drawerRelate.buttonRelate') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog @keydown.esc="successDialog=false" v-model="successDialog" persistent max-width="400px">
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text>
          {{ $t('expenses.registerMovements.invoices.drawerRelate.successDialog.text') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="closeSuccessDialog">
            {{ $t('expenses.registerMovements.invoices.drawerRelate.successDialog.buttonAccept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>

export default {
  name: 'RelatePayOrder',
  props: {
    isOpen: Boolean,
    invoice: Object
  },
  emits: ['updatePayOrders', 'updateInvoices'],
  data () {
    return {
      overlay: false,
      successDialog: false,
      table: {
        title: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.headers.id'), value: '_id', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.headers.amount'), value: 'total' },
          { text: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.headers.paymentMode'), value: 'paymentMethod', sortable: false },
          { text: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.headers.date'), value: 'createdAt' },
          { text: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.headers.status'), value: 'status' }
        ],
        data: [],
        total: 0,
        itemsPerPage: 10,
        page: 1,
        selected: []
      },
      PAYORDER_PAYMENT_TYPES: {
        balance: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.paymentTypes.balance'),
        credit: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.paymentTypes.credit')
      },
      PAYORDER_STATUS_TYPES: {
        created: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.statusTypes.created'),
        pending_payment: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.statusTypes.pendingPayment'),
        paid: this.$i18n.t('expenses.registerMovements.invoices.drawerRelate.statusTypes.paid')
      }
    }
  },
  async mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
    await this.loadSimilarPayOrders()
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  watch: {
    invoice: function () {
      this.loadSimilarPayOrders()
    }
  },
  computed: {
    restaurantTaxid () {
      return this.$store.state.currentRestaurant.taxInformation.data ? (this.$store.state.currentRestaurant.taxInformation.data.taxId || '') : ''
    },
    open: {
      get: function () {
        return this.isOpen
      },
      set: function (newValue) {
        this.$emit('update:isOpen', newValue)
      }
    },
    invoiceBoxPermissions () {
      return this.$store.state.login.permissions['expenses'].components['financial-movements'].actions
    }
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') this.open = false
    },
    loadSimilarPayOrders: async function (params = null) {
      params = params || {
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      if (this.invoice && this.invoice.total) {
        params.amount = this.invoice.total
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/expenses/${this.$store.state.currentRestaurant.data._id}`, params)
        this.table.data = response
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    relatePayOrder: async function () {
      const payload = {
        order: this.table.selected[0]._id,
        rfc: this.restaurantTaxid,
        isIssuer: false
      }
      this.overlay = true
      try {
        await this.$api.put(`/v1/invoices/${this.invoice.uuid}`, payload)
        this.$emit('updateInvoices')
        this.$emit('updatePayOrders')
        this.openSuccessDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    openEditDialog (item) {
      this.payOrderToEdit = item
      this.editDialog = true
    },
    closeEditDialog () {
      this.payOrderToEdit = {}
      this.editDialog = false
    },
    openPanel () {
      this.open = true
    },
    closePanel () {
      this.open = false
    },
    openSuccessDialog () {
      this.successDialog = true
      this.open = false
    },
    closeSuccessDialog () {
      this.successDialog = false
    }
  }
}
</script>
